import {
  BankCardActionResponse,
  BankCoinsActionResponse,
  BankPayFeeResponse,
  BankPoint,
  BankStatus,
  BankTermDeposit,
  BankTermDepositActionResponse,
  BankTermDepositPlans,
  BankTermDepositStatus,
  BankTermDepositSummary,
  BankUpgradeResponse,
} from "../types/bank"
import { GameCard } from "../types/battle"
import { normalizeGameCard, normalizeGameCardsList } from "./gameCards"

type RawRecord = Record<string, unknown>

const DEFAULT_RADIUS_M = 50
const DEFAULT_UPGRADE_PRICE = 5000
const DEFAULT_UPGRADE_SLOTS = 25
const DEFAULT_MAX_UPGRADES = 2
const DEFAULT_MONTHLY_FEE = 10

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed || fallback
  }
  if (value != null) {
    const asString = String(value).trim()
    return asString || fallback
  }
  return fallback
}

function pickNumber(value: unknown, fallback: number | null = null): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value.trim().replace(",", "."))
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1") return true
  if (value === 0 || value === "0") return false
  return fallback
}

function unwrapPayload(data: unknown): RawRecord {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as RawRecord
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as RawRecord), ...obj }
  }
  return obj
}

export function normalizeBankPoint(raw: unknown): BankPoint | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const item = raw as RawRecord
  const id = item.id ?? item.uuid ?? item.pointId ?? item.point_id
  const lat = pickNumber(item.latitude ?? item.lat)
  const lng = pickNumber(item.longitude ?? item.lng ?? item.lon)
  if (id == null || lat == null || lng == null) return null

  return {
    id: String(id),
    name: pickString(item.name ?? item.title, `Банк ${id}`),
    latitude: lat,
    longitude: lng,
    radiusMeters:
      pickNumber(
        item.radiusMeters ?? item.radius_meters ?? item.radiusM ?? item.radius,
        DEFAULT_RADIUS_M
      ) ?? DEFAULT_RADIUS_M,
  }
}

export function mapBankPoints(data: unknown): BankPoint[] {
  if (typeof data === "string" && data.trim()) {
    try {
      return mapBankPoints(JSON.parse(data))
    } catch {
      return []
    }
  }
  if (Array.isArray(data)) {
    return data
      .map(normalizeBankPoint)
      .filter((point): point is BankPoint => point != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.points ?? obj.items ?? obj.banks ?? obj.bankPoints ?? obj.bank_points
  if (Array.isArray(list)) {
    return list
      .map(normalizeBankPoint)
      .filter((point): point is BankPoint => point != null)
  }
  const single = normalizeBankPoint(obj)
  return single ? [single] : []
}

export function normalizeBankStatus(data: unknown): BankStatus | null {
  const obj = unwrapPayload(data)
  const bankCapacity =
    pickNumber(obj.bankCapacity ?? obj.bank_capacity ?? obj.capacity)
  if (bankCapacity == null) return null

  const bankCardsCount =
    pickNumber(obj.bankCardsCount ?? obj.bank_cards_count ?? obj.cardsCount, 0) ?? 0
  const bankSlotsLeft =
    pickNumber(
      obj.bankSlotsLeft ?? obj.bank_slots_left ?? Math.max(0, bankCapacity - bankCardsCount),
      Math.max(0, bankCapacity - bankCardsCount)
    ) ?? Math.max(0, bankCapacity - bankCardsCount)

  const upgradeSlots =
    pickNumber(obj.upgradeSlots ?? obj.upgrade_slots, DEFAULT_UPGRADE_SLOTS) ??
    DEFAULT_UPGRADE_SLOTS
  const bankUpgradesPurchased =
    pickNumber(
      obj.bankUpgradesPurchased ?? obj.bank_upgrades_purchased ?? obj.upgradesPurchased,
      0
    ) ?? 0
  const maxUpgrades =
    pickNumber(obj.maxUpgrades ?? obj.max_upgrades, DEFAULT_MAX_UPGRADES) ??
    DEFAULT_MAX_UPGRADES
  const canUpgrade = pickBool(
    obj.canUpgrade ?? obj.can_upgrade,
    bankUpgradesPurchased < maxUpgrades
  )
  const nextUpgradeCapacity = pickNumber(
    obj.nextUpgradeCapacity ?? obj.next_upgrade_capacity,
    canUpgrade ? bankCapacity + upgradeSlots : null
  )

  const termRaw =
    obj.termDeposits && typeof obj.termDeposits === "object"
      ? (obj.termDeposits as RawRecord)
      : null
  const termDeposits: BankTermDepositSummary | undefined = termRaw
    ? {
        activeCount:
          pickNumber(termRaw.activeCount ?? termRaw.active_count, 0) ?? 0,
        lockedPrincipal:
          pickNumber(
            termRaw.lockedPrincipal ?? termRaw.locked_principal,
            0
          ) ?? 0,
        maxActive:
          pickNumber(termRaw.maxActive ?? termRaw.max_active, 3) ?? 3,
      }
    : undefined

  return {
    bankCoins: pickNumber(obj.bankCoins ?? obj.bank_coins, 0) ?? 0,
    bankCapacity,
    bankCardsCount,
    bankSlotsLeft,
    bankUpgradesPurchased,
    canUpgrade,
    nextUpgradeCapacity,
    upgradePriceCoins:
      pickNumber(
        obj.upgradePriceCoins ?? obj.upgrade_price_coins,
        DEFAULT_UPGRADE_PRICE
      ) ?? DEFAULT_UPGRADE_PRICE,
    upgradeSlots,
    maxUpgrades,
    monthlyFeeCoins:
      pickNumber(
        obj.monthlyFeeCoins ?? obj.monthly_fee_coins,
        DEFAULT_MONTHLY_FEE
      ) ?? DEFAULT_MONTHLY_FEE,
    bankFeeDebt: pickNumber(obj.bankFeeDebt ?? obj.bank_fee_debt, 0) ?? 0,
    termDeposits,
  }
}

const TERM_STATUSES: BankTermDepositStatus[] = [
  "ACTIVE",
  "CLAIMED",
  "EARLY_CLOSED",
]

function normalizeTermStatus(value: unknown): BankTermDepositStatus | null {
  const s = pickString(value).toUpperCase()
  return TERM_STATUSES.includes(s as BankTermDepositStatus)
    ? (s as BankTermDepositStatus)
    : null
}

export function normalizeBankTermDeposit(raw: unknown): BankTermDeposit | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as RawRecord
  const id = pickString(obj.id)
  const principal = pickNumber(obj.principal)
  const rateBps = pickNumber(obj.rateBps ?? obj.rate_bps)
  const termDays = pickNumber(obj.termDays ?? obj.term_days)
  const unlockAt = pickString(obj.unlockAt ?? obj.unlock_at)
  const status = normalizeTermStatus(obj.status)
  if (!id || principal == null || rateBps == null || termDays == null || !unlockAt || !status) {
    return null
  }

  const interestFull =
    pickNumber(obj.interestFull ?? obj.interest_full) ??
    Math.floor((principal * rateBps) / 10000)
  const interestEarly =
    pickNumber(obj.interestEarly ?? obj.interest_early) ??
    Math.floor(interestFull * 0.5)
  const unlocked = pickBool(
    obj.unlocked,
    Date.now() >= new Date(unlockAt).getTime()
  )
  const canClaim = pickBool(obj.canClaim ?? obj.can_claim, status === "ACTIVE" && unlocked)
  const canEarlyClose = pickBool(
    obj.canEarlyClose ?? obj.can_early_close,
    status === "ACTIVE" && !unlocked
  )

  return {
    id,
    principal,
    rateBps,
    ratePercent: pickNumber(obj.ratePercent ?? obj.rate_percent, rateBps / 100) ?? rateBps / 100,
    termDays,
    unlockAt,
    status,
    payoutCoins: pickNumber(obj.payoutCoins ?? obj.payout_coins) ?? null,
    interestPaid: pickNumber(obj.interestPaid ?? obj.interest_paid) ?? null,
    closedAt: pickString(obj.closedAt ?? obj.closed_at) || null,
    bankPointId: pickString(obj.bankPointId ?? obj.bank_point_id) || null,
    createAt: pickString(obj.createAt ?? obj.create_at ?? obj.createdAt),
    interestFull,
    interestEarly,
    payoutFull:
      pickNumber(obj.payoutFull ?? obj.payout_full, principal + interestFull) ??
      principal + interestFull,
    payoutEarly:
      pickNumber(obj.payoutEarly ?? obj.payout_early, principal + interestEarly) ??
      principal + interestEarly,
    unlocked,
    canClaim,
    canEarlyClose,
  }
}

export function normalizeBankTermDeposits(data: unknown): BankTermDeposit[] {
  if (Array.isArray(data)) {
    return data
      .map(normalizeBankTermDeposit)
      .filter((d): d is BankTermDeposit => d != null)
  }
  const obj = unwrapPayload(data)
  const list = obj.deposits ?? obj.items ?? obj.termDeposits ?? obj.term_deposits
  if (Array.isArray(list)) {
    return list
      .map(normalizeBankTermDeposit)
      .filter((d): d is BankTermDeposit => d != null)
  }
  return []
}

export function normalizeBankTermDepositPlans(
  data: unknown
): BankTermDepositPlans | null {
  const obj = unwrapPayload(data)
  const plansRaw = obj.plans
  if (!Array.isArray(plansRaw) || plansRaw.length === 0) return null
  const plans = plansRaw
    .map((raw) => {
      if (!raw || typeof raw !== "object") return null
      const p = raw as RawRecord
      const termDays = pickNumber(p.termDays ?? p.term_days)
      const rateBps = pickNumber(p.rateBps ?? p.rate_bps)
      if (termDays == null || rateBps == null) return null
      return {
        termDays,
        rateBps,
        ratePercent:
          pickNumber(p.ratePercent ?? p.rate_percent, rateBps / 100) ??
          rateBps / 100,
      }
    })
    .filter((p): p is NonNullable<typeof p> => p != null)
  if (plans.length === 0) return null
  return {
    plans,
    minAmount: pickNumber(obj.minAmount ?? obj.min_amount, 100) ?? 100,
    maxAmount: pickNumber(obj.maxAmount ?? obj.max_amount, 5000) ?? 5000,
    maxActivePerUser:
      pickNumber(obj.maxActivePerUser ?? obj.max_active_per_user, 3) ?? 3,
    earlyInterestFactor:
      pickNumber(
        obj.earlyInterestFactor ?? obj.early_interest_factor,
        0.5
      ) ?? 0.5,
  }
}

export function normalizeBankTermDepositActionResponse(
  data: unknown
): BankTermDepositActionResponse | null {
  const obj = unwrapPayload(data)
  const deposit = normalizeBankTermDeposit(obj.deposit)
  if (!deposit) return null
  const coins = pickNumber(obj.coins)
  if (coins == null) return null
  return {
    deposit,
    coins,
    status: normalizeBankStatus(obj.status ?? obj.bankStatus) ?? undefined,
    payoutCoins: pickNumber(obj.payoutCoins ?? obj.payout_coins) ?? undefined,
    interestPaid:
      pickNumber(obj.interestPaid ?? obj.interest_paid) ?? undefined,
  }
}

export function normalizeBankCards(data: unknown): GameCard[] {
  return normalizeGameCardsList(data).map((card) => ({
    ...card,
    storedInBank: true,
  }))
}

export function normalizeBankCoinsActionResponse(
  data: unknown
): BankCoinsActionResponse | null {
  const obj = unwrapPayload(data)
  const status = normalizeBankStatus(obj.status ?? obj.bankStatus ?? obj)
  const bankCoins =
    pickNumber(obj.bankCoins ?? obj.bank_coins, status?.bankCoins) ?? status?.bankCoins
  const coins =
    pickNumber(obj.coins ?? obj.walletCoins ?? obj.wallet_coins) ??
    pickNumber(obj.passport && typeof obj.passport === "object"
      ? (obj.passport as RawRecord).coins
      : undefined)
  if (bankCoins == null) return null
  return {
    bankCoins,
    coins: coins ?? undefined,
    status: status ?? undefined,
  }
}

export function normalizeBankCardActionResponse(
  data: unknown
): BankCardActionResponse | null {
  const obj = unwrapPayload(data)
  const card = normalizeGameCard(obj.card)
  if (!card) return null
  return {
    card,
    status: normalizeBankStatus(obj.status ?? obj.bankStatus) ?? undefined,
    coins: pickNumber(obj.coins) ?? undefined,
  }
}

export function normalizeBankUpgradeResponse(
  data: unknown
): BankUpgradeResponse | null {
  const obj = unwrapPayload(data)
  const status = normalizeBankStatus(obj.status ?? obj.bankStatus ?? obj)
  if (!status) return null
  const coins =
    pickNumber(obj.coins) ??
    pickNumber(
      obj.passport && typeof obj.passport === "object"
        ? (obj.passport as RawRecord).coins
        : undefined
    ) ??
    0
  return { status, coins }
}

export function normalizeBankPayFeeResponse(
  data: unknown
): BankPayFeeResponse | null {
  return normalizeBankUpgradeResponse(data)
}

export function isBankStoredCard(card: Pick<GameCard, "storedInBank">): boolean {
  return card.storedInBank === true
}

export function excludeBankStoredCards<T extends Pick<GameCard, "storedInBank">>(
  cards: T[]
): T[] {
  return cards.filter((card) => !isBankStoredCard(card))
}
